"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

interface ChangelogLatestScrollProps {
  targetId: string;
  /** Query param to strip after scrolling so refresh doesn't re-trigger. */
  param: string;
}

/**
 * Scrolls the newest changelog entry into view when arriving from the
 * post-update prompt (`?highlight=latest`), then cleans the URL.
 */
export function ChangelogLatestScroll({ targetId, param }: ChangelogLatestScrollProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const el = document.getElementById(targetId);
    if (el) {
      // Let layout settle (header offset + fonts) before scrolling.
      const id = window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [targetId]);

  useEffect(() => {
    if (!searchParams.has(param)) return;
    const next = new URLSearchParams(searchParams.toString());
    next.delete(param);
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [param, pathname, router, searchParams]);

  return null;
}
